import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

const currency = typeof Intl !== 'undefined'
  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
  : null;

const formatMoney = (value) => {
  if (value === null || value === undefined) return '';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return '';
  return currency ? currency.format(num) : `$${num.toFixed(2)}`;
};

const emptyMaterial = () => ({
  item: '',
  quantity: '',
  unit: '',
  cost: '',
  notes: '',
  source: '',
  product_link: '',
});

export default function CommunityBuilds() {
  const { user } = useAuth();
  const [builds, setBuilds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [voteBusy, setVoteBusy] = useState({});
  const [form, setForm] = useState({
    title: '',
    description: '',
    renderUrl: '',
    materials: [emptyMaterial()],
  });

  const totalCost = useMemo(() => {
    return form.materials.reduce((sum, m) => {
      const cost = Number(m.cost);
      return Number.isFinite(cost) ? sum + cost : sum;
    }, 0);
  }, [form.materials]);

  const loadBuilds = useCallback(async (query = '') => {
    setLoading(true);
    try {
      const { data } = await api.get('/community-builds', {
        params: query ? { q: query } : undefined,
      });
      const next = (data?.builds || []).map((b) => ({
        ...b,
        vote_count: Number(b.vote_count) || 0,
        user_voted: false,
      }));
      setBuilds(next);
    } catch (err) {
      console.error('loadBuilds', err);
      toast.error(err?.response?.data?.error || 'Unable to load community builds.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBuilds();
  }, [loadBuilds]);

  useEffect(() => {
    const handle = setTimeout(() => {
      loadBuilds(search.trim());
    }, 250);
    return () => clearTimeout(handle);
  }, [search, loadBuilds]);

  const updateMaterial = (index, patch) => {
    setForm((prev) => {
      const next = prev.materials.slice();
      next[index] = { ...next[index], ...patch };
      return { ...prev, materials: next };
    });
  };

  const addMaterial = () => setForm((prev) => ({ ...prev, materials: [...prev.materials, emptyMaterial()] }));
  const removeMaterial = (idx) => {
    setForm((prev) => {
      const next = prev.materials.filter((_, i) => i !== idx);
      return { ...prev, materials: next.length ? next : [emptyMaterial()] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = form.title.trim();
    if (!title) {
      toast.error('Title is required.');
      return;
    }
    const materials = form.materials
      .map((m) => ({
        item: m.item.trim(),
        quantity: m.quantity,
        unit: m.unit,
        notes: m.notes,
        source: m.source,
        product_link: m.product_link,
        cost: m.cost === '' ? null : Number(m.cost),
      }))
      .filter((m) => m.item || m.notes || m.cost !== null);

    setPosting(true);
    try {
      await api.post('/community-builds', {
        title,
        description: form.description,
        renderUrl: form.renderUrl,
        materials,
      });
      toast.success('Shared to Community Builds!');
      setForm({ title: '', description: '', renderUrl: '', materials: [emptyMaterial()] });
      loadBuilds(search.trim());
    } catch (err) {
      console.error('submit build', err);
      toast.error(err?.response?.data?.error || 'Unable to share build.');
    } finally {
      setPosting(false);
    }
  };

  const toggleVote = async (buildId) => {
    if (!user) {
      toast.error('Sign in to vote.');
      return;
    }
    const build = builds.find((b) => b.id === buildId);
    if (!build) return;
    const action = build.user_voted ? 'delete' : 'post';
    setVoteBusy((prev) => ({ ...prev, [buildId]: true }));
    try {
      const { data } = await api[action](`/community-builds/${buildId}/vote`);
      setBuilds((prev) =>
        prev.map((b) =>
          b.id === buildId
            ? { ...b, vote_count: Number(data.vote_count) || 0, user_voted: data.voted }
            : b
        )
      );
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Unable to vote.');
    } finally {
      setVoteBusy((prev) => ({ ...prev, [buildId]: false }));
    }
  };

  return (
    <>
    <div className="mx-auto max-w-6xl px-4 py-8 text-zinc-100">
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold">Community Builds</h1>
          <p className="text-sm text-zinc-400">Share and browse builds from the Design Assistant.</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title..."
            className="w-48 sm:w-64 rounded-lg bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
          />
          <button
            type="button"
            onClick={() => setShowForm((s) => !s)}
            className="rounded-lg border border-white/15 px-3 py-2 text-sm text-zinc-200 hover:bg-white/5 transition"
          >
            {showForm ? 'Close' : '+ Add Build'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="glass-card mb-8 p-5">
          <h2 className="text-xl font-semibold mb-3">Share a Build</h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm text-zinc-400 block mb-1">Title *</label>
                <input
                  className="w-full rounded-lg bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Modern cabin living room"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 block mb-1">Render URL (optional)</label>
                <input
                  className="w-full rounded-lg bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                  value={form.renderUrl}
                  onChange={(e) => setForm({ ...form, renderUrl: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-zinc-400 block mb-1">Description (optional)</label>
              <textarea
                className="w-full rounded-lg bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Share notes about the build, constraints, or goals."
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-200">Materials & Costs</h3>
                <button
                  type="button"
                  className="text-sm text-amber-300 hover:text-amber-200"
                  onClick={addMaterial}
                >
                  + Add item
                </button>
              </div>
              <div className="space-y-3">
                {form.materials.map((m, idx) => (
                  <div
                    key={idx}
                    className="grid gap-2 rounded-lg border border-white/5 bg-black/30 px-3 py-3 sm:grid-cols-12"
                  >
                    <input
                      className="sm:col-span-3 rounded-md bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                      placeholder="Item"
                      value={m.item}
                      onChange={(e) => updateMaterial(idx, { item: e.target.value })}
                    />
                    <input
                      className="sm:col-span-2 rounded-md bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                      placeholder="Quantity"
                      value={m.quantity}
                      onChange={(e) => updateMaterial(idx, { quantity: e.target.value })}
                    />
                    <input
                      className="sm:col-span-2 rounded-md bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                      placeholder="Unit"
                      value={m.unit}
                      onChange={(e) => updateMaterial(idx, { unit: e.target.value })}
                    />
                    <input
                      className="sm:col-span-2 rounded-md bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                      placeholder="Cost"
                      value={m.cost}
                      onChange={(e) => updateMaterial(idx, { cost: e.target.value })}
                    />
                    <input
                      className="sm:col-span-2 rounded-md bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                      placeholder="Source"
                      value={m.source}
                      onChange={(e) => updateMaterial(idx, { source: e.target.value })}
                    />
                    <input
                      className="sm:col-span-3 rounded-md bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                      placeholder="Product link"
                      value={m.product_link}
                      onChange={(e) => updateMaterial(idx, { product_link: e.target.value })}
                    />
                    <textarea
                      className="sm:col-span-8 rounded-md bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100"
                      placeholder="Notes"
                      rows={2}
                      value={m.notes}
                      onChange={(e) => updateMaterial(idx, { notes: e.target.value })}
                    />
                    <div className="sm:col-span-4 flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() => removeMaterial(idx)}
                        className="text-xs text-red-300 hover:text-red-200"
                        disabled={form.materials.length === 1}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {totalCost > 0 && (
                <div className="text-right text-sm text-zinc-300">Total: {formatMoney(totalCost)}</div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="rounded-lg bg-gradient-to-r from-amber-300 to-amber-500 px-4 py-2 text-sm font-semibold text-zinc-900 shadow-lg disabled:opacity-60"
                disabled={posting}
              >
                {posting ? 'Sharing...' : 'Share Build'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          <p className="text-sm text-zinc-400">Loading builds...</p>
        ) : builds.length === 0 ? (
          <p className="text-sm text-zinc-400">No community builds yet. Be the first to share!</p>
        ) : (
          builds.map((build) => (
            <div
              key={build.id}
              className="glass-card rounded-xl border border-white/10 p-4 shadow-[0_8px_30px_rgba(0,0,0,0.25)] hover:border-amber-400/30 transition"
            >
              <Link to={`/community-builds/${build.id}`} className="flex items-center gap-3 group">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-amber-200 truncate group-hover:text-amber-100 transition">
                      {build.title}
                    </h3>
                    {build.total_cost && (
                      <span className="rounded-full bg-amber-500/10 px-3 py-1 text-xs text-amber-200 border border-amber-400/30">
                        {formatMoney(build.total_cost)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-400 truncate">
                    Posted by {build.username || 'unknown'} · {build.created_at ? new Date(build.created_at).toLocaleString() : ''}
                  </p>
                  {Array.isArray(build.tags) && build.tags.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-2">
                      {build.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="text-[11px] uppercase tracking-wide rounded-full bg-white/5 px-2 py-1 text-zinc-300 border border-white/10"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {build.render_url && (
                  <div className="h-16 w-24 flex-shrink-0 overflow-hidden rounded-md border border-white/10 bg-black/40">
                    <img
                      src={build.render_url}
                      alt={build.title}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
              </Link>
              <div className="mt-3 flex items-center gap-2 text-sm text-zinc-300">
                <button
                  type="button"
                  onClick={() => toggleVote(build.id)}
                  disabled={voteBusy[build.id]}
                  className={
                    build.user_voted
                      ? 'rounded-full border px-3 py-1 text-xs border-amber-400/50 bg-amber-500/10 text-amber-200'
                      : 'rounded-full border px-3 py-1 text-xs border-white/10 bg-white/5 text-zinc-200 hover:border-amber-300/60 hover:text-amber-200'
                  }
                >
                  {build.user_voted ? 'Upvoted' : 'Upvote'}
                </button>
                <span className="text-xs text-zinc-400">
                  {build.vote_count === 1 ? '1 vote' : `${build.vote_count} votes`}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
    </>
  );
}
